// Quy tắc báo cáo công việc hằng ngày: khung giờ nhắc nộp báo cáo (admin cấu hình, đồng bộ với portal) và tính "đang ở nhịp
// nhắc nào". Module thuần (chỉ cú pháp TS xóa được) để chạy được trong unit test của Node.

export interface ReportConfig {
  /** Giờ bắt đầu nhắc, "HH:MM". */
  start: string;
  /** Giờ kết thúc nhắc, "HH:MM" (nhịp nhắc cuối không muộn hơn mốc này). */
  end: string;
  /** Cách bao nhiêu phút nhắc lại nếu chưa nộp. */
  intervalMin: number;
}

export const DEFAULT_REPORT_CONFIG: ReportConfig = { start: '17:30', end: '21:30', intervalMin: 30 };
export const INTERVAL_CHOICES = [15, 30, 45, 60] as const;

const HM = /^([01]?\d|2[0-3]):([0-5]\d)$/;

/** "17:30" → số phút từ 0h; sai định dạng → NaN. */
export function parseHM(s: string): number {
  const m = HM.exec(s.trim());
  return m ? Number(m[1]) * 60 + Number(m[2]) : Number.NaN;
}

/** Trả thông báo lỗi tiếng Việt, hoặc `undefined` nếu cấu hình hợp lệ. */
export function configError(c: ReportConfig): string | undefined {
  const a = parseHM(c.start);
  const b = parseHM(c.end);
  if (Number.isNaN(a) || Number.isNaN(b)) return 'Giờ phải có dạng HH:MM (ví dụ 17:30).';
  if (b <= a) return 'Giờ kết thúc phải sau giờ bắt đầu.';
  if (!Number.isInteger(c.intervalMin) || c.intervalMin < 5 || c.intervalMin > 180) return 'Khoảng nhắc lại từ 5 đến 180 phút.';
  return undefined;
}

export interface ReminderState {
  /** Đang trong khung giờ nhắc. */
  inWindow: boolean;
  /** Số thứ tự nhịp nhắc hiện tại (0 = lúc bắt đầu, 1 = sau một khoảng…); -1 nếu ngoài khung. */
  slot: number;
  /** Số nhịp nhắc trong ngày (kể cả nhịp đầu). */
  slots: number;
}

/** Khung nhắc tại thời điểm `now` (giờ máy). */
export function reminderState(now: Date, c: ReportConfig): ReminderState {
  const a = parseHM(c.start);
  const b = parseHM(c.end);
  if (configError(c) !== undefined) return { inWindow: false, slot: -1, slots: 0 };
  const mins = now.getHours() * 60 + now.getMinutes();
  const slots = Math.floor((b - a) / c.intervalMin) + 1;
  if (mins < a || mins > b) return { inWindow: false, slot: -1, slots };
  return { inWindow: true, slot: Math.floor((mins - a) / c.intervalMin), slots };
}

/** Nên hiện chip nhắc không: trong khung giờ, chưa nộp hôm nay và chưa tắt chip ở nhịp này. */
export function shouldRemind(now: Date, c: ReportConfig, submittedToday: boolean, dismissedSlot: number | undefined): boolean {
  if (submittedToday) return false;
  const st = reminderState(now, c);
  return st.inWindow && dismissedSlot !== st.slot;
}

/** Lần nhắc kế tiếp (chưa nộp) sau `now`; `undefined` nếu hôm nay hết nhịp. */
export function nextReminderAt(now: Date, c: ReportConfig): Date | undefined {
  if (configError(c) !== undefined) return undefined;
  const a = parseHM(c.start);
  const b = parseHM(c.end);
  const mins = now.getHours() * 60 + now.getMinutes();
  for (let t = a; t <= b; t += c.intervalMin) {
    if (t > mins) return new Date(now.getFullYear(), now.getMonth(), now.getDate(), Math.floor(t / 60), t % 60);
  }
  return undefined;
}

export interface ReportItemInput {
  progress: number;
}
/** Tiến độ trung bình các đầu việc (0–100), làm tròn; rỗng → 0. */
export function averageProgress(items: ReportItemInput[]): number {
  if (!items.length) return 0;
  return Math.round(items.reduce((s, i) => s + Math.min(100, Math.max(0, i.progress)), 0) / items.length);
}
